import type { DataTableColumn, DataTableRenderCellParams } from '@/components/custom/erp/data-table-v2';
import { InputSearch } from '@/components/custom/erp/data-table-v2/search-inputs';
import { formatDate, formatMoney, formatRoundedNumber } from '@/lib/formatUtils';
import { Checkbox } from '@/components/ui/checkbox';

export const getDataTableColumns = (
  handleOpenViewForm: (row: MuaHangRecord) => void,
  totalAmount?: number | null,
  isTotalLoading?: boolean
): DataTableColumn[] => [
  {
    field: 'status',
    headerName: 'Trạng thái',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => {
      switch (params.row.status) {
        case '0':
          return 'Chưa ghi sổ';
        case '3':
          return 'Chờ duyệt';
        case '5':
          return 'Đã ghi sổ';
        default:
          return '';
      }
    }
  },
  {
    field: 'so_ct',
    headerName: 'Số c/từ',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <div
        className='cursor-pointer hover:text-blue-600 hover:underline'
        onClick={e => {
          e.stopPropagation();
          handleOpenViewForm(params.row);
        }}
      >
        {params.row.so_ct}
      </div>
    )
  },
  {
    field: 'ngay_ct',
    headerName: 'Ngày c/từ',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => formatDate(params.row.ngay_ct)
  },
  {
    field: 'ma_kh',
    headerName: 'Mã khách hàng',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_kh_data?.customer_code
  },
  {
    field: 'ten_kh',
    headerName: 'Tên khách hàng',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_kh_data?.customer_name
  },
  { field: 'dien_giai', headerName: 'Diễn giải', width: 200 },
  {
    field: 'tk',
    headerName: 'Tk có',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.tk_data?.code
  },
  {
    field: 't_tt_nt',
    headerName: 'Tổng tiền',
    width: 160,
    type: 'number',
    renderCell: (params: DataTableRenderCellParams) => formatRoundedNumber(params.row.t_tt_nt),
    renderSearchInput: params => (
      <InputSearch
        {...params}
        onKeyDown={params.onKeyDown ?? (() => {})}
        suffixContent={
          <div className='flex items-center gap-2'>
            <span className='text-lg font-bold'>&#931;</span>
            <span className='font-semibold text-main'>
              {isTotalLoading
                ? 'Đang tính...'
                : typeof totalAmount === 'number'
                  ? formatRoundedNumber(totalAmount)
                  : '—'}
            </span>
          </div>
        }
      />
    )
  },
  {
    field: 'qt_tu_yn',
    headerName: 'Quyết toán',
    width: 120,
    type: 'boolean',
    renderCell: (params: DataTableRenderCellParams) => <Checkbox checked={params.row.qt_tu_yn} disabled />
  },
  {
    field: 't_tien_cl_nt',
    headerName: 'Chênh lệch quyết toán',
    width: 160,
    type: 'number',
    renderCell: (params: DataTableRenderCellParams) => formatMoney(params.row.t_tien_cl_nt)
  },
  {
    field: 'ma_ct_qt',
    headerName: 'C.từ quyết toán',
    width: 120,
    type: 'number',
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_ct_qt
  },
  {
    field: 'ma_nt',
    headerName: 'Ngoại tệ',
    width: 100,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_nt_data.ma_nt
  },
  {
    field: 'ma_ngv',
    headerName: 'Loại tạm ứng',
    width: 150,
    renderCell: params => {
      switch (params.row.ma_ngv) {
        case '2':
          return '2. Tạm ứng thông thường';
        case '3':
          return '3. Cấn trừ công nợ';
        default:
          return '';
      }
    }
  }
];

export const getTableDetailColumns: DataTableColumn[] = [
  // Base fields - always visible
  {
    field: 'tk_no',
    headerName: 'Tài khoản nợ',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.tk_no_data?.code
  },
  {
    field: 'ten_tk',
    headerName: 'Tên tài khoản',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => params.row.tk_no_data?.name
  },
  {
    field: 'ma_kh',
    headerName: 'Mã khách hàng',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_kh_data?.customer_code
  },
  {
    field: 'ten_kh',
    headerName: 'Tên khách hàng',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_kh_data?.customer_name
  },
  { field: 'dien_giai', headerName: 'Diễn giải', width: 200 },
  {
    field: 'ma_loai_hd',
    headerName: 'Loại hoá đơn',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => {
      switch (params.row.ma_loai_hd) {
        case '0':
          return '0. Không có hoá đơn';
        case '1':
          return '1. Hoá đơn GTGT đã tách thuế';
        case '2':
          return '2. Hoá đơn GTGT không tách thuế';
        case '3':
          return '3. Hoá đơn bán hàng thông thường';
        default:
          return '';
      }
    }
  },
  // Tax and invoice fields - conditional based on ma_loai_hd
  {
    field: 'ma_thue',
    headerName: 'Mã thuế',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_thue_data?.ma_thue
  },
  {
    field: 'tk_thue',
    headerName: 'Tk thuế',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.tk_thue_data?.code
  },
  {
    field: 'thue_suat',
    headerName: 'Thuế suất',
    width: 120,
    type: 'number',
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_thue_data?.thue_suat
  },
  {
    field: 'so_ct0',
    headerName: 'Số hoá đơn',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.so_ct0
  },
  {
    field: 'so_ct2',
    headerName: 'Ký hiệu',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.so_ct2
  },
  {
    field: 'ngay_ct0',
    headerName: 'Ngày hoá đơn',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ngay_ct0
  },
  {
    field: 'ma_mau_ct',
    headerName: 'Mẫu HĐ',
    width: 120
  },
  {
    field: 'ma_mau_bc',
    headerName: 'Mẫu báo cáo',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => {
      switch (params.row.ma_mau_bc) {
        case '3':
          return '3. Hóa đơn giá trị gia tăng';
        case '4':
          return '4. Hàng hóa, dịch vụ mua vào không có hóa đơn';
        case '5':
          return '5. Hóa đơn bán hàng thông thường';
        default:
          return '';
      }
    }
  },
  {
    field: 'ma_tc_thue',
    headerName: 'Mã tính chất',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => {
      // Hiển thị từ ma_tc_thue_data nếu có, fallback về ma_tc_thue
      const maTcThue = params.row.ma_tc_thue_data?.ma_tc_thue || params.row.ma_tc_thue;
      const tenTcThue = params.row.ma_tc_thue_data?.ten_tc_thue;

      // Nếu có tên tính chất thuế từ data, hiển thị mã + tên
      if (tenTcThue) {
        return `${maTcThue} - ${tenTcThue}`;
      }

      // Fallback về switch case cũ cho backward compatibility
      switch (maTcThue) {
        case '1':
          return '1. Hàng hoá, dịch vụ dùng riêng cho SXKD chịu thuế GTGT và sử dụng cho các hoạt động cung cấp hàng hoá, dịch vụ không kê khai, nộp thuế GTGT đủ điều kiện khấu trừ thuế';
        case '2':
          return '2. Hàng hoá, dịch vụ dùng chung cho SXKD chịu thuế GTGT và không chịu thuế GTGT';
        case '3':
          return '3. Hàng hóa, dịch vụ dùng cho dự án đầu tư đủ điều kiện khấu trừ thuế';
        case '4':
          return '4. Hàng hóa, dịch vụ không đủ điều kiện khấu trừ';
        case '5':
          return '5. Hàng hóa, dịch vụ không phải tổng hợp trên tờ khai 01/GTGT';
        default:
          return '';
      }
    }
  },
  {
    field: 'ma_kh_thue',
    headerName: 'Mã ncc',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_kh_thue_data?.customer_code
  },
  {
    field: 'ten_kh_thue',
    headerName: 'Tên nhà cung cấp',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_kh_thue_data?.customer_name
  },
  {
    field: 'dia_chi',
    headerName: 'Địa chỉ',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_kh_thue_data?.address
  },
  {
    field: 'ma_so_thue',
    headerName: 'Mã số thuế',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_kh_thue_data?.tax_code
  },
  {
    field: 'ma_kh9',
    headerName: 'Cục thuế',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_kh9_data?.customer_code
  },
  {
    field: 'ten_kh9',
    headerName: 'Tên cục thuế',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_kh9_data?.customer_name
  },
  // Organizational fields
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_bp_data?.ma_bp
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_vv_data?.ma_vv
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_hd_data?.ma_hd
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_dtt_data?.ma_dtt
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_ku_data?.ma_ku
  },
  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_phi_data?.ma_phi
  },
  {
    field: 'ma_sp',
    headerName: 'Sản phẩm',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_sp_data?.ma_vt
  },
  {
    field: 'ma_lsx',
    headerName: 'Lệnh sản xuất',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_lsx_data?.ma_lsx
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_cp0_data?.ma_cp
  }
];

export const historyColumns: DataTableColumn[] = [
  { field: 'username', headerName: 'Tài khoản', width: 100 },
  { field: 'nickname', headerName: 'Tên tài khoản', width: 200 },
  { field: 'action', headerName: 'Hành động', width: 100 },
  { field: 'datetime0', headerName: 'Thời gian', width: 150 }
];
